import {Attributes} from 'platform/components';
import {HStack, Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {Nullish} from 'utility-types';

import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, RequiredTestIdProps, parseDate, suffixTestId} from 'shared/web';

const AI_REPLACEMENT_PART_SHAPE = {
  nextDate: null as string | null,
  nextMileage: null as number | null,
  afterMonths: null as number | null,
  afterMileage: null as number | null,
};

type AiReplacementPart = typeof AI_REPLACEMENT_PART_SHAPE;

interface VehicleServiceIntervalReplacementAttributesProps extends RequiredTestIdProps {
  part:
    | {
        lastReplacementDate: string | Nullish;
        replacementAfterMonths: number | Nullish;
        nextReplacementDate: string | Nullish;
        lastReplacementMileage: number | Nullish;
        replacementAfterMileage: number | Nullish;
        nextReplacementMileage: number | Nullish;
      }
    | Nullish;
  aiPart?: AiReplacementPart | Nullish;
}

export function VehicleServiceIntervalReplacementAttributes(
  props: VehicleServiceIntervalReplacementAttributesProps
) {
  const formatDateTime = useDateTimeFormatter();
  const hasAiPart = Boolean(props.aiPart);

  return (
    <Attributes
      rows={[
        {
          label: i18n.t('entity.vehicle.labels.dateOfLastReplacement'),
          ...(hasAiPart
            ? {
                content: (
                  <HStack justify="space-between">
                    <Text size="xSmall">
                      {props.part?.lastReplacementDate
                        ? formatDateTime('dateShort', parseDate(props.part.lastReplacementDate))
                        : EMPTY_PLACEHOLDER}
                    </Text>
                    <Text size="xSmall">{EMPTY_PLACEHOLDER}</Text>
                  </HStack>
                ),
              }
            : {
                value: props.part?.lastReplacementDate
                  ? formatDateTime('dateShort', parseDate(props.part.lastReplacementDate))
                  : null,
              }),
        },
        {
          label: i18n.t('entity.vehicle.labels.replacementAfterMonths'),
          ...(hasAiPart
            ? {
                content: (
                  <HStack justify="space-between">
                    <Text size="xSmall">
                      {props.part?.replacementAfterMonths ?? EMPTY_PLACEHOLDER}
                    </Text>
                    <Text size="xSmall">{props.aiPart?.afterMonths ?? EMPTY_PLACEHOLDER}</Text>
                  </HStack>
                ),
              }
            : {value: props.part?.replacementAfterMonths}),
        },
        {
          label: i18n.t('entity.vehicle.labels.nextReplacementDate'),
          ...(hasAiPart
            ? {
                content: (
                  <HStack justify="space-between">
                    <Text size="xSmall">
                      {props.part?.nextReplacementDate
                        ? formatDateTime('dateShort', parseDate(props.part.nextReplacementDate))
                        : EMPTY_PLACEHOLDER}
                    </Text>
                    <Text size="xSmall">
                      {props.aiPart?.nextDate
                        ? formatDateTime('dateShort', parseDate(props.aiPart.nextDate))
                        : EMPTY_PLACEHOLDER}
                    </Text>
                  </HStack>
                ),
              }
            : {
                value: props.part?.nextReplacementDate
                  ? formatDateTime('dateShort', parseDate(props.part.nextReplacementDate))
                  : null,
              }),
        },
        {
          label: `${i18n.t('entity.vehicle.labels.mileageAtLastReplacement')} (${i18n.t('general.metric.km')})`,
          ...(hasAiPart
            ? {
                content: (
                  <HStack justify="space-between">
                    <Text size="xSmall">
                      {props.part?.lastReplacementMileage ?? EMPTY_PLACEHOLDER}
                    </Text>
                    <Text size="xSmall">{EMPTY_PLACEHOLDER}</Text>
                  </HStack>
                ),
              }
            : {value: props.part?.lastReplacementMileage}),
        },
        {
          label: `${i18n.t('entity.vehicle.labels.replacementAfterMileage')} (${i18n.t('general.metric.km')})`,
          ...(hasAiPart
            ? {
                content: (
                  <HStack justify="space-between">
                    <Text size="xSmall">
                      {props.part?.replacementAfterMileage ?? EMPTY_PLACEHOLDER}
                    </Text>
                    <Text size="xSmall">{props.aiPart?.afterMileage ?? EMPTY_PLACEHOLDER}</Text>
                  </HStack>
                ),
              }
            : {value: props.part?.replacementAfterMileage}),
        },
        {
          label: `${i18n.t('entity.vehicle.labels.nextReplacementMileage')} (${i18n.t('general.metric.km')})`,
          ...(hasAiPart
            ? {
                content: (
                  <HStack justify="space-between">
                    <Text size="xSmall">
                      {props.part?.nextReplacementMileage ?? EMPTY_PLACEHOLDER}
                    </Text>
                    <Text size="xSmall">{props.aiPart?.nextMileage ?? EMPTY_PLACEHOLDER}</Text>
                  </HStack>
                ),
              }
            : {value: props.part?.nextReplacementMileage}),
        },
      ]}
      data-testid={suffixTestId('part', props)}
    />
  );
}
